import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../../../contexts/BusinessContext';
import PrinterHeader from './PrinterHeader';
import PrinterClientBottomSheet from './PrinterClientBottomSheet';
import PrinterStatusAlerts from './PrinterStatusAlerts';
import ConfiguredPrintersCard from './ConfiguredPrintersCard';
import PrinterSearchModal from './PrinterSearchModal';
import PrinterConfigModal from './PrinterConfigModal';
import { usePrinterManager } from '../../hooks/usePrinterManager';
import { Plus } from 'lucide-react';

const PrinterManager = () => {
  const { business, businessLoading } = useBusiness();
  const navigate = useNavigate();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const {
    // Estado
    showConfig,
    selectedPrinter,
    printerConfig,
    configuredPrinters,
    printersLoading,
    printersError,
    wsStatus,
    clientStatus,
    wsError,
    foundPrinters,
    testError,
    testingPrinter,

    // Handlers
    handleConfigurePrinter,
    handleTestPrinter,
    handleSavePrinter,
    handleCloseConfig,
    setPrinterConfig,
    scanNetwork,
    checkClientStatus,
    handleTestNewPrinter
  } = usePrinterManager();

  // Debug logs
  useEffect(() => {
    console.log('=== DEBUG PRINTER MANAGER ===');
    console.log('Business:', {
      id: business?.id,
      name: business?.name,
      owner_id: business?.owner_id
    });
    console.log('WS Status:', wsStatus);
    console.log('Client Status:', clientStatus);
    console.log('Printers:', configuredPrinters);
    console.log('WS Error:', wsError);
  }, [business, wsStatus, clientStatus, configuredPrinters, wsError]);

  useEffect(() => {
    console.log('🖨️ PrinterManager - Estado actualizado:', {
      clientStatus,
      scanning: wsStatus === 'scanning',
      scanDisabled: wsStatus === 'scanning' || clientStatus !== 'running',
      component: 'PrinterManager',
      timestamp: new Date().toISOString()
    });
  }, [clientStatus, wsStatus]);

  if (businessLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Cargando...</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">No se encontró el negocio</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PrinterHeader 
        businessName={business?.name}
        wsStatus={wsStatus}
        clientStatus={clientStatus}
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <PrinterStatusAlerts 
          wsError={wsError}
          clientStatus={clientStatus}
          printersError={printersError}
          onCheckStatus={checkClientStatus}
        />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Impresoras Configuradas
            </h2>
            <button
              onClick={() => setShowSearchModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Plus className="w-4 h-4" />
              <span>Agregar Impresora</span>
            </button>
          </div>
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <ConfiguredPrintersCard 
              printers={configuredPrinters}
              loading={printersLoading}
              onTestPrinter={handleTestPrinter}
              onConfigurePrinter={handleConfigurePrinter}
            />
          </div>
        </div>

        <PrinterSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onScan={scanNetwork}
          scanning={wsStatus === 'scanning'}
          foundPrinters={foundPrinters}
          onConfigurePrinter={handleConfigurePrinter}
          onTestPrinter={handleTestPrinter}
          clientStatus={clientStatus}
        />

        <PrinterConfigModal
          printer={selectedPrinter}
          open={showConfig}
          onClose={handleCloseConfig}
          onSave={handleSavePrinter}
          config={printerConfig}
          onConfigChange={setPrinterConfig}
          testError={testError}
          handleTestNewPrinter={handleTestNewPrinter}
          testingPrinter={testingPrinter}
          onScan={scanNetwork}
          scanning={wsStatus === 'scanning'}
          clientStatus={clientStatus}
        />
      </div>

      <PrinterClientBottomSheet />
    </div>
  );
};

export default PrinterManager; 